'use client';

import React, { useState } from 'react';
import { ChevronUp, Clock, Filter, TrendingUp, Sparkles, CheckCircle2 } from 'lucide-react';

const ALL_GOSSIP = [
  { id: 'v-001', text: 'El dueño del negocio de la esquina tiene dos familias en la misma cuadra y ninguna sabe de la otra 🤯', category: 'Vecinos', votes: 567, status: 'trending', time: 'hace 4h' },
  { id: 'v-002', text: 'El marido de mi amiga dice que trabaja hasta tarde pero lo vieron en el mall con otra señora comprando ropa de mujer... para él 💅', category: 'Familia', votes: 445, status: 'en-cola', time: 'hace 2h' },
  { id: 'v-003', text: 'Una amiga se fue de vacaciones con su "primo" y resulta que el primo tiene el mismo apellido que ella pero no son familia 🤔', category: 'Amor', votes: 312, status: 'en-cola', time: 'hace 5h' },
  { id: 'v-004', text: 'Mi vecina del 3ro lleva 3 semanas diciéndole a todos que está a dieta pero la pillé comprando 2 tortas en la pastelería... 👀', category: 'Vecinos', votes: 234, status: 'pendiente', time: 'hace 6h' },
  { id: 'v-005', text: 'Mi jefe llegó a la reunión con la camisa al revés y nadie le dijo nada en toda la mañana 😂', category: 'Trabajo', votes: 189, status: 'contado', time: 'hace 1d' },
  { id: 'v-006', text: 'Mi compañera de trabajo lleva 6 meses diciéndole a todos que está embarazada para no hacer nada... y no está embarazada 😱', category: 'Trabajo', votes: 156, status: 'pendiente', time: 'hace 8h' },
  { id: 'v-007', text: 'El profe de yoga del barrio tiene 3 alumnas que creen que son su polola y ninguna sabe de las otras dos 🧘‍♂️', category: 'Amor', votes: 143, status: 'pendiente', time: 'hace 10h' },
  { id: 'v-008', text: 'Mi tía lleva 10 años diciendo que viajará a Europa "el próximo año" y nunca ha salido de la Región Metropolitana ✈️', category: 'Familia', votes: 98, status: 'contado', time: 'hace 2d' },
  { id: 'v-009', text: 'El presidente del condominio usa los fondos del edificio para pagar el Netflix de su casa 😤', category: 'Vecinos', votes: 267, status: 'en-cola', time: 'hace 3h' },
  { id: 'v-010', text: 'Una famosa influencer de Santiago compra sus propios productos con cuentas falsas para inflar las reseñas ⭐', category: 'Famosos', votes: 389, status: 'trending', time: 'hace 1h' },
];

const TABS = [
  { id: 'tab-todos', label: 'Todos', icon: Sparkles, filter: 'todos' },
  { id: 'tab-trending', label: 'Trending 🔥', icon: TrendingUp, filter: 'trending' },
  { id: 'tab-nuevos', label: 'Nuevos', icon: Clock, filter: 'nuevos' },
  { id: 'tab-contados', label: 'Ya Contados ✅', icon: CheckCircle2, filter: 'contado' },
];

const categoryColors: Record<string, string> = {
  Amor: 'bg-pink-100 text-pink-700',
  Trabajo: 'bg-blue-100 text-blue-700',
  Familia: 'bg-orange-100 text-orange-700',
  Vecinos: 'bg-green-100 text-green-700',
  Famosos: 'bg-purple-100 text-purple-700',
  Otro: 'bg-gray-100 text-gray-700',
};

const statusLabels: Record<string, { label: string; cls: string }> = {
  trending: { label: '🔥 Trending', cls: 'bg-red-100 text-red-700 border border-red-200' },
  'en-cola': { label: '⏳ En Cola', cls: 'status-en-cola' },
  pendiente: { label: '📬 Pendiente', cls: 'status-pendiente' },
  contado: { label: '✅ Contado', cls: 'status-contado' },
};

export default function VotePageFeed() {
  const [activeTab, setActiveTab] = useState('todos');
  const [sortBy, setSortBy] = useState<'votos' | 'recientes'>('votos');
  const [votes, setVotes] = useState<Record<string, number>>(
    Object.fromEntries(ALL_GOSSIP.map((g) => [g.id, g.votes]))
  );
  const [voted, setVoted] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState(1);
  const PER_PAGE = 6;

  const handleVote = (id: string) => {
    if (voted[id]) return;
    setVotes((prev) => ({ ...prev, [id]: prev[id] + 1 }));
    setVoted((prev) => ({ ...prev, [id]: true }));
  };

  const filtered = ALL_GOSSIP.filter((g) => {
    if (activeTab === 'todos') return true;
    if (activeTab === 'trending') return g.status === 'trending';
    if (activeTab === 'contado') return g.status === 'contado';
    if (activeTab === 'nuevos') return g.status === 'pendiente';
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'votos') return votes[b.id] - votes[a.id];
    return 0;
  });

  const paginated = sorted.slice(0, page * PER_PAGE);
  const hasMore = paginated.length < sorted.length;

  return (
    <section className="bg-muted/30 py-16">
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h2 className="text-2xl font-bold text-foreground">Todos los Chismes 🗳️</h2>
          <div className="flex items-center gap-2">
            <Filter size={15} className="text-muted-foreground" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'votos' | 'recientes')}
              className="bg-card border border-border rounded-xl px-3 py-2 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="votos">Más votados</option>
              <option value="recientes">Más recientes</option>
            </select>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-1">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.filter); setPage(1); }}
              className={`shrink-0 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                activeTab === tab.filter
                  ? 'bg-primary text-white shadow-sm'
                  : 'bg-card border border-border text-muted-foreground hover:text-primary hover:border-primary/30'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        {paginated.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-5xl mb-4">🤷‍♀️</div>
            <h3 className="text-lg font-bold text-foreground mb-2">No hay chismes aquí</h3>
            <p className="text-muted-foreground text-sm">Sé el primero en enviar un chisme en esta categoría</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {paginated.map((gossip) => {
              const statusInfo = statusLabels[gossip.status];
              return (
                <div
                  key={gossip.id}
                  className={`bg-card border border-border rounded-2xl p-5 shadow-sm card-hover flex flex-col gap-3 ${
                    gossip.status === 'trending' ? 'ring-2 ring-primary/20' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${categoryColors[gossip.category] || categoryColors['Otro']}`}>
                      {gossip.category}
                    </span>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 ${statusInfo.cls}`}>
                      {statusInfo.label}
                    </span>
                  </div>
                  <p className="text-sm text-foreground leading-relaxed flex-1 font-medium">{gossip.text}</p>
                  <div className="flex items-center justify-between pt-2 border-t border-border">
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock size={11} />
                      {gossip.time}
                    </span>
                    <button
                      onClick={() => handleVote(gossip.id)}
                      disabled={voted[gossip.id] || gossip.status === 'contado'}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all duration-150 ${
                        voted[gossip.id]
                          ? 'bg-primary/10 text-primary cursor-default'
                          : gossip.status === 'contado' ?'bg-muted text-muted-foreground cursor-default' :'bg-primary text-white hover:bg-primary/90 active:scale-95 shadow-sm'
                      }`}
                    >
                      <ChevronUp size={15} />
                      <span className="font-tabular">{votes[gossip.id]}</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {hasMore && (
          <div className="mt-8 text-center">
            <button
              onClick={() => setPage((p) => p + 1)}
              className="btn-secondary px-8 py-3 rounded-xl font-semibold text-sm"
            >
              Cargar más chismes 👇
            </button>
          </div>
        )}
      </div>
    </section>
  );
}